import { AppDispatch } from '../../redux/store';
import { CalendarEventObject } from '../../params/redux/calendar';
import CardWrapper from '../wrapper/card';
import DeleteCalendarLinkModal from './modal/delete_calendar_link';
import DuplicateCalendarLinkModal from './modal/duplicate_calendar_link';
import { formatDate } from '../../helper';
import ImageClass from '../../assets';
import { settingFormFieldsInCalendarBuilder } from '../../redux/slices/calendar_form_builder';
import ShareEventModal from './modal/share_event';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { AccessTimeOutlined, ContentCopyOutlined, CopyAll, Delete, Edit, InsertDriveFileOutlined, MoreVert, NotificationsNoneOutlined, ShareOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { brand, green, orange, red } from '../../theme/themePrimitives';
import { settingAddingGoogleEvent, settingAddingGoogleMeeting, settingEditEventFormFlag, settingEditNotificationFlag, settingSelectedEvent } from '../../redux/slices/calendar';

interface LinkEventComponentProps {
    event: CalendarEventObject;
}

const LinkCardComponent = ({ event }: LinkEventComponentProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>();
    const [shareEvent, setShareEvent] = useState<number>(0);
    const [deleteEvent, setDeleteEvent] = useState<number>(0);
    const [duplicateEvent, setDuplicateEvent] = useState<number>(0);

    const handleMoreButton = (buttonEvent: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(buttonEvent.currentTarget);
    };

    const editNotificationHandler = () => {
        dispatch(settingSelectedEvent(event));
        dispatch(settingEditNotificationFlag(true));
    };

    const deleteEventHandler = () => {
        dispatch(settingSelectedEvent(event));
        setDeleteEvent(event.id);
    };

    const duplicateEventHandler = () => {
        dispatch(settingSelectedEvent(event));
        setDuplicateEvent(event.id);
    };

    const editCalendarLinkHandler = () => {
        dispatch(settingSelectedEvent(event));
        dispatch(settingAddingGoogleEvent(true));
        dispatch(settingAddingGoogleMeeting(false));
    };

    const editEventFormHandler = () => {
        dispatch(settingFormFieldsInCalendarBuilder({
                formTemplateId: 2,
                fields: event.form_json ? JSON.parse(event.form_json).fields : []
            }));
        dispatch(settingSelectedEvent(event));
        dispatch(settingEditEventFormFlag(true));
    };

    const onCloseHandler = () => {
        dispatch(settingSelectedEvent(null));
        setShareEvent(0);
        setDuplicateEvent(0);
        setDeleteEvent(0);
    };

    return (
        <CardWrapper>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" gap={1} alignItems="center">
                    <Box
                        width={12}
                        height={12}
                        borderRadius="50%"
                        bgcolor={event.color_tag ? event.color_tag === 'primary' ? brand[300] : event.color_tag === 'success' ? green[400] : event.color_tag === 'warning' ? orange[300] : red[300] : brand[300]}
                    />
                    <Typography variant="h6" fontWeight="700" color="textSecondary">
                        {event.event_name || 'Untitled Event'}
                    </Typography>
                </Box>
                <Box>
                    <IconButton sx={{ display: 'none' }} onClick={() => setShareEvent(event.id)}><ShareOutlined color="disabled" /></IconButton>
                    <IconButton onClick={handleMoreButton}><MoreVert color="disabled" /></IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                        MenuListProps={{
                            'aria-labelledby': 'add-pipeline-button'
                        }}
                    >
                        <MenuItem onClick={editCalendarLinkHandler}>
                            <Box display="flex" alignItems="center" gap={2}><Edit color="disabled" /> <Typography color="textSecondary" variant="body2">Edit Calendar Link</Typography></Box>
                        </MenuItem>
                        <MenuItem sx={{ display: 'none' }} onClick={duplicateEventHandler}>
                            <Box display="flex" alignItems="center" gap={2}><ContentCopyOutlined color="disabled" /> <Typography color="textSecondary" variant="body2">Duplicate Calendar Link</Typography></Box>
                        </MenuItem>
                        <MenuItem onClick={editNotificationHandler}>
                            <Box display="flex" alignItems="center" gap={2}><NotificationsNoneOutlined color="disabled" /> <Typography color="textSecondary" variant="body2">Edit Notifications</Typography></Box>
                        </MenuItem>
                        <MenuItem onClick={editEventFormHandler}>
                            <Box display="flex" alignItems="center" gap={2}><InsertDriveFileOutlined color="disabled" /> <Typography color="textSecondary" variant="body2">Edit Event Form</Typography></Box>
                        </MenuItem>
                        <MenuItem onClick={deleteEventHandler}>
                            <Box display="flex" alignItems="center" gap={2}><Delete color="disabled" /> <Typography color="textSecondary" variant="body2">Delete Calendar Link</Typography></Box>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>
            <Typography variant="body2" color="textSecondary" my={1}>
                {formatDate(event.start_date || '2025-01-24T05:00:00+05:00', {
                    month: 'long',
                    year: 'numeric',
                    day: '2-digit'
                }) || 'No Date'} - {formatDate(event.end_date || '2025-01-24T05:00:00+05:00', {
                    month: 'long',
                    year: 'numeric',
                    day: '2-digit'
                }) || 'No Date'}
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                {event.platform === 'meet' && <Box display="flex" alignItems="center">
                    <img src={ImageClass.googleMeetLogo} height={30} width={50} />
                    <Typography variant="body1" color="textSecondary">Google Meeting</Typography>
                </Box>}
                <Box display="flex" gap={1} alignItems="center">
                    <AccessTimeOutlined color="disabled" />
                    <Typography variant="body1" color="textSecondary">{`${event.duration} min`}</Typography>
                </Box>
            </Box>
            <Box display="flex" justifyContent="space-between">
                <Button variant="text" size="small" sx={{ display: 'none' }}>
                    Copy Embed Code
                </Button>
                <Button
                    variant="text"
                    aria-label={`${window.location.origin}/booking/${event.unique_id}`}
                    startIcon={<CopyAll />}
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/booking/${event.unique_id}`)}
                >Copy Link</Button>
            </Box>
            {shareEvent === event.id && <ShareEventModal open={shareEvent === event.id} onClose={onCloseHandler} />}
            {duplicateEvent === event.id && <DuplicateCalendarLinkModal open={duplicateEvent === event.id} onClose={onCloseHandler} />}
            {deleteEvent === event.id && <DeleteCalendarLinkModal open={deleteEvent === event.id} onClose={onCloseHandler} />}
        </CardWrapper>
    );
};

export default LinkCardComponent;